// ConfirmActionModal — generic confirm dialog for stage transitions
// and sub-flag REQUESTS that don't need a custom field set beyond
// optional notes (Phase 6B).
//
// Receives the full AvailableJobAction DTO so the modal title, body,
// and submit-button label come straight from the server. The
// destination endpoint + method come from actionKeyEndpoints.

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { apiClient } from '../../../../api/client';
import { queryKeys } from '../../../../api/queryKeys';
import type { AvailableJobAction } from '../../../../types/api';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import {
  buildActionUrl,
  getActionKeyConfig,
} from './actionKeyEndpoints';

import styles from './ProcessActionModal.module.css';

export interface ConfirmActionModalProps {
  isOpen: boolean;
  jobEncId: string;
  action: AvailableJobAction;
  onSuccess?: () => void;
  onClose: () => void;
}

export function ConfirmActionModal({
  isOpen,
  jobEncId,
  action,
  onSuccess,
  onClose,
}: ConfirmActionModalProps): JSX.Element | null {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = getActionKeyConfig(action.action_key);

  async function handleSubmit() {
    setError(null);
    if (config === null || !config.endpoint || !config.method) {
      setError(`No endpoint configured for action "${action.action_key}".`);
      return;
    }
    setSubmitting(true);
    try {
      const url = buildActionUrl(config.endpoint, jobEncId);
      const body: Record<string, unknown> = { ...(config.body ?? {}) };
      if (notes.trim()) body['notes'] = notes.trim();
      if (config.method === 'PATCH') {
        await apiClient.patch(url, body);
      } else {
        await apiClient.post(url, body);
      }
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(jobEncId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobs.availableActions(jobEncId),
      });
      onSuccess?.();
      onClose();
      setNotes('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title={action.modal_title ?? action.button_label}
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={submitting}>
            {action.button_label}
          </Button>
        </div>
      }
    >
      <p className={styles.body}>{action.modal_body ?? 'Are you sure?'}</p>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="confirm-action-notes">
          Notes (optional)
        </label>
        <textarea
          id="confirm-action-notes"
          className={styles.textarea}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
    </Modal>
  );
}
